"use client";

import { Box, Collapse, colors, Typography } from "@mui/material";
import { HEADER_HEIGHT, TOP_BAR_HEIGHT } from "@v2/components/header/constants";
import { useAppSelector } from "@v2/lib/redux/hooks";
import { AlignLeft, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export type TocItem = { id: string; text: string; level: 2 | 3 };

// Where the pinned bar comes to rest: under the top bar, and under the site header
// as well while that one is open. Same offsets the plp toolbar parks at.
const STICKY_TOP = TOP_BAR_HEIGHT;
const STICKY_TOP_WITH_HEADER = HEADER_HEIGHT.mobile + TOP_BAR_HEIGHT;

// The bar is this tall whatever it is saying. A nested h3 adds a second line naming its
// parent h2, and without a fixed height that would both resize the pinned bar mid-scroll
// and, because the bar's placeholder sits back at the top of the article, shove the whole
// article down with it. One line simply centres in the same box.
const BAR_HEIGHT = 54;

// The line down the page that decides which heading counts as the one being read, and
// where a tapped heading comes to rest. It clears the pinned bar at its lowest — under
// an open site header — so a heading never lands behind it.
const ACTIVE_LINE = STICKY_TOP_WITH_HEADER + BAR_HEIGHT + 16;

type Props = {
    items: TocItem[];
    /**
     * The mobile variant: starts closed, pins itself to the top of the screen once it is
     * reached, names the section being read and fills its own background as the article
     * is read through. The desktop sidebar renders the plain open list instead.
     */
    collapsible?: boolean;
};

export default function BlogToc({ items, collapsible = false }: Props) {
    const headerIsShow = useAppSelector((s) => s.general.headerIsShow);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [open, setOpen] = useState(!collapsible);
    const [stuck, setStuck] = useState(false);
    const [barTop, setBarTop] = useState(0);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const pausedRef = useRef(false);
    const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelPendingOpenRef = useRef<(() => void) | null>(null);

    // A tap-driven jump is held until the page actually stops moving. A fixed timeout
    // cannot do this: a long smooth scroll outlives it, and the reading resumes mid-flight
    // so the bar ticks through every heading it flies past before settling on the one
    // that was tapped. Each scroll event pushes the release back instead.
    const holdUntilSettled = useCallback(() => {
        pausedRef.current = true;
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(() => { pausedRef.current = false; }, 150);
    }, []);

    const stickyTop = headerIsShow ? STICKY_TOP_WITH_HEADER : STICKY_TOP;

    useEffect(() => {
        if (items.length < 2) return;

        const update = () => {
            if (pausedRef.current) {
                holdUntilSettled();
            } else {
                // null until the first heading is actually reached, so the bar keeps calling
                // itself the index while the reader is still in the intro
                const line = window.scrollY + ACTIVE_LINE;
                let current: string | null = null;
                for (const { id } of items) {
                    const el = document.getElementById(id);
                    if (el && el.offsetTop <= line) current = id;
                }
                setActiveId(current);
            }

            if (!collapsible) return;

            // read the sentinel, not the bar: the bar's own top animates for half a second
            // whenever the site header opens or closes, and would report the wrong state
            // for as long as no further scroll event arrives to correct it.
            const sentinel = sentinelRef.current;
            if (sentinel) {
                const top = sentinel.getBoundingClientRect().top;
                // A pixel of slack. The sentinel comes to rest on a fractional offset — the
                // content above it does not add up to whole pixels — so an exact comparison
                // reads a bar that is plainly pinned as still loose, and it keeps its rounded
                // corners and page gutter instead of going edge to edge.
                setStuck(top <= stickyTop + 1);
                setBarTop(Math.max(stickyTop, Math.round(top)));
            }

        };

        update();
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [items, collapsible, stickyTop, holdUntilSettled]);

    // The article is frozen while the list is open rather than the list being dismissed by
    // the first scroll: a long list is taller than the screen and has to be scrollable in
    // its own right, and a scroll it provokes itself must not close it.
    useEffect(() => {
        if (!collapsible || !open) return;
        // body only. html carries overflow visible, so body's value is what propagates to
        // the viewport — and locking html instead collapses the scrollable area outright,
        // which throws the reader back to the top of the article.
        const { body } = document;
        const previous = body.style.overflow;
        body.style.overflow = "hidden";
        return () => { body.style.overflow = previous; };
    }, [collapsible, open]);

    // keep the section being read in sight once the open list has to scroll on its own
    useEffect(() => {
        if (!open || !activeId) return;
        const list = listRef.current;
        if (!list || list.scrollHeight <= list.clientHeight) return;
        const el = list.querySelector<HTMLElement>(`[data-toc-id="${activeId}"]`);
        if (!el) return;
        list.scrollTop = Math.max(0, el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2);
    }, [activeId, open]);

    // The site header is left exactly as it is. It used to duck away as the list opened and
    // pop back as it closed, but only because the list pushed the article down and the page
    // scrolled itself; the list floats now, so neither happens. Driving it from here is not
    // an option either — the header owns that state and only publishes it to the store.
    const toggle = useCallback(() => {
        // a second tap lands while the bar is still travelling to the top
        cancelPendingOpenRef.current?.();

        if (open) {
            setOpen(false);
            return;
        }

        // Pin the bar first. Opened where it still sits in the article the list would get
        // whatever is left of the screen below it — a couple of hundred pixels — and the
        // page is about to be frozen, so it could not be scrolled into view.
        //
        // Aim at the lowest line the bar can rest on, not at today's. Scrolling down may
        // well send the site header away, and then a scroll that stopped at the header's
        // line leaves the bar hanging below the top bar instead of pinned. Overshooting is
        // free: with the header still up, sticky just holds at 92.
        const sentinel = sentinelRef.current;
        const delta = sentinel ? sentinel.getBoundingClientRect().top - STICKY_TOP : 0;
        if (delta <= 0) {
            setOpen(true);
            return;
        }

        window.scrollBy({ top: delta, behavior: "smooth" });

        // Open once the page has come to rest. Freezing it mid-flight would cut the smooth
        // scroll short, and opening straight away would make the travel read as a jump.
        let debounce = setTimeout(finish, 400);
        const onScroll = () => {
            clearTimeout(debounce);
            debounce = setTimeout(finish, 120);
        };
        const fallback = setTimeout(finish, 1200);

        function finish() {
            cancelPendingOpenRef.current?.();
            setOpen(true);
        }

        cancelPendingOpenRef.current = () => {
            window.removeEventListener("scroll", onScroll);
            clearTimeout(debounce);
            clearTimeout(fallback);
            cancelPendingOpenRef.current = null;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
    }, [open]);

    useEffect(() => () => cancelPendingOpenRef.current?.(), []);

    const scrollTo = useCallback((id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - ACTIVE_LINE;

        setActiveId(id);
        if (collapsible) setOpen(false);
        holdUntilSettled();

        window.scrollTo({ top, behavior: "smooth" });

        el.style.color = "#d32f2f";
        setTimeout(() => {
            el.style.transition = "color 1s ease-out";
            el.style.color = "";
            setTimeout(() => { el.style.transition = ""; }, 1100);
        }, 1500);
    }, [collapsible, holdUntilSettled]);

    if (items.length < 2) return null;

    // Once pinned over a section the bar stops advertising itself and reports position.
    // A second line appears only for an h3, to name the h2 it sits under; an h2 says
    // everything it needs to on one.
    const activeIndex = items.findIndex((item) => item.id === activeId);
    const active = activeIndex >= 0 ? items[activeIndex] : null;
    // Open, the bar is pinned by construction: it is scrolled to its resting place before
    // the list is allowed to appear. Tying the pinned look to that as well as to the
    // measurement keeps the two from ever disagreeing.
    const pinned = collapsible && (stuck || open);
    const showPosition = collapsible && stuck && !!active;
    const parent = showPosition && active!.level === 3
        ? items.slice(0, activeIndex).reverse().find((item) => item.level === 2) ?? null
        : null;

    const floating = collapsible
        ? {
            position: "absolute" as const,
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1,
            bgcolor: "#fff",
            borderRadius: pinned ? 0 : "0 0 12px 12px",
            boxShadow: "0 12px 24px -12px rgba(15, 23, 42, 0.28)",
        }
        : undefined;

    return (
        <>
            {/* Zero height marker left behind in the flow. The bar itself moves once it pins,
                so only this reports where the bar's resting place actually is. */}
            <Box ref={sentinelRef} aria-hidden sx={{ height: 0 }} />

            {collapsible && (
                // Softens the article behind the open list, and catches the tap that
                // dismisses it. Sits under the bar but over everything else.
                <Box
                    aria-hidden
                    onClick={() => setOpen(false)}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 89,
                        bgcolor: "rgba(255, 255, 255, 0.4)",
                        backdropFilter: "blur(3px)",
                        touchAction: "none",
                        opacity: open ? 1 : 0,
                        pointerEvents: open ? "auto" : "none",
                        transition: "opacity 220ms",
                    }}
                />
            )}

            <Box sx={{
                mb: 2,
                ...(collapsible && {
                    // the mobile-only switch lives here rather than on a wrapper: a wrapper would
                    // be the sticky containing block, and at 69px tall it leaves nowhere to stick.
                    display: { xs: "block", lg: "none" },
                    position: "sticky",
                    top: stickyTop,
                    zIndex: 90,
                    transition: "top 0.5s cubic-bezier(0.22,0.61,0.36,1)",
                }),
            }}>
                <Box sx={{
                    position: "relative",
                    bgcolor: "#fff",
                    // pinned it stops being a card: edge to edge, square, lifted off the article
                    mx: pinned ? -2 : 0,
                    borderRadius: pinned ? 0 : 1.5,
                    boxShadow: pinned ? "0 2px 10px rgba(15, 23, 42, 0.08)" : "none",
                    transition: "margin 200ms, border-radius 200ms, box-shadow 200ms",
                }}>
                    <Box
                        component={collapsible ? "button" : "div"}
                        type={collapsible ? "button" : undefined}
                        onClick={collapsible ? toggle : undefined}
                        aria-expanded={collapsible ? open : undefined}
                        sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            width: 1,
                            px: 2,
                            py: collapsible ? 0 : 2,
                            minHeight: collapsible ? BAR_HEIGHT : "auto",
                            border: 0,
                            borderRadius: "inherit",
                            overflow: "hidden",
                            bgcolor: "transparent",
                            font: "inherit",
                            textAlign: "inherit",
                            cursor: collapsible ? "pointer" : "default",
                            // no blue tap flash / selection tint on the header
                            WebkitTapHighlightColor: "transparent",
                            userSelect: "none",
                            outline: "none",
                            "&:focus": { outline: "none" },
                            "&:focus-visible": { outline: "none" },
                        }}
                    >
                        <Box sx={{
                            position: "relative",
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}>
                            {showPosition ? (
                                <Box
                                    key={active!.id}
                                    sx={{
                                        minWidth: 0,
                                        animation: "tocSwap 240ms ease-out",
                                        "@keyframes tocSwap": {
                                            from: { opacity: 0, transform: "translateY(6px)" },
                                            to: { opacity: 1, transform: "none" },
                                        },
                                    }}
                                >
                                    {parent && (
                                        <Typography component="span" sx={{
                                            display: "block",
                                            fontSize: 11,
                                            lineHeight: 1.4,
                                            color: colors.grey[500],
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}>
                                            {parent.text}
                                        </Typography>
                                    )}
                                    <Typography component="span" sx={{
                                        display: "block",
                                        fontSize: 13,
                                        fontWeight: 700,
                                        lineHeight: 1.4,
                                        color: colors.grey[800],
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}>
                                        {active!.text}
                                    </Typography>
                                </Box>
                            ) : (
                                // Idle: it is still just the index, and says how much of one it is.
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                    <Box sx={{ display: "flex", flexShrink: 0 }}>
                                        <AlignLeft size={16} color={colors.grey[600]} />
                                    </Box>
                                    <Typography component="span" sx={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        lineHeight: 1.5,
                                        color: colors.grey[700],
                                        whiteSpace: "nowrap",
                                    }}>
                                        فهرست مطالب
                                    </Typography>
                                    {collapsible && (
                                        <Typography component="span" sx={{
                                            fontSize: 12,
                                            lineHeight: 1.5,
                                            color: colors.grey[500],
                                            flexShrink: 0,
                                        }}>
                                            ({items.length} بخش)
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {collapsible && (
                                <Box sx={{
                                    display: "flex",
                                    flexShrink: 0,
                                    transform: open ? "rotate(180deg)" : "none",
                                    transition: "transform 200ms",
                                }}>
                                    <ChevronDown size={18} color={colors.grey[600]} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Collapse in={open} unmountOnExit={false} sx={floating}>
                        <Box
                            ref={listRef}
                            component="ul"
                            sx={{
                                listStyle: "none",
                                m: 0,
                                px: 2,
                                pb: 2,
                                pt: collapsible ? 1 : 0,
                                // floating over the page, the list has only the screen to work with
                                maxHeight: collapsible
                                    ? `calc(100dvh - ${barTop + BAR_HEIGHT + 24}px)`
                                    : "none",
                                overflowY: collapsible ? "auto" : "visible",
                                overscrollBehavior: "contain",
                            }}
                        >
                            {items.map((item) => {
                                const isActive = activeId === item.id;
                                return (
                                    <Box
                                        component="li"
                                        key={item.id}
                                        data-toc-id={item.id}
                                        onClick={() => scrollTo(item.id)}
                                        sx={{
                                            borderRadius: 1,
                                            py: 0.4,
                                            pl: item.level === 3 ? 2 : 1,
                                            pr: 1,
                                            cursor: "pointer",
                                            WebkitTapHighlightColor: "transparent",
                                            bgcolor: isActive ? colors.red[50] : "transparent",
                                            transition: "background-color 200ms",
                                            "&:hover": {
                                                bgcolor: isActive ? colors.red[50] : "rgba(0,0,0,0.04)",
                                            },
                                        }}
                                    >
                                        <Typography component="span" sx={{
                                            fontSize: item.level === 2 ? 13 : 12,
                                            color: isActive
                                                ? colors.grey[900]
                                                : item.level === 2
                                                    ? colors.grey[800]
                                                    : colors.grey[600],
                                            fontWeight: isActive ? 700 : 500,
                                            lineHeight: 1.5,
                                            transition: "color 150ms",
                                        }}>
                                            {item.level === 3 && (
                                                <Box component="span" sx={{
                                                    display: "inline-block",
                                                    width: 5,
                                                    height: 5,
                                                    borderRadius: "50%",
                                                    bgcolor: isActive ? colors.red[400] : colors.red[200],
                                                    verticalAlign: "middle",
                                                    mr: 1,
                                                    mb: "2px",
                                                    flexShrink: 0,
                                                    transition: "background-color 150ms",
                                                }} />
                                            )}
                                            {item.text}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        </>
    );
}
